import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { auth } from './Firebase';
import { useAuth } from '../contexts/AuthContext';

const planLabels = {
  plus: 'Symuliga Plus',
  plus_monthly: 'Symuliga Plus',
  plus_yearly: 'Symuliga Plus',
  regions: 'Symuliga Regionalna',
  regions_monthly: 'Symuliga Regionalna',
  regions_yearly: 'Symuliga Regionalna',
};

const intervalLabels = {
  plus_monthly: 'Miesięczna',
  regions_monthly: 'Miesięczna',
  plus_yearly: 'Roczna',
  regions_yearly: 'Roczna',
};

const statusLabels = {
  active: 'Aktywna',
  trialing: 'Okres próbny',
  past_due: 'Płatność wymaga uwagi',
  canceled: 'Anulowana',
  unpaid: 'Nieopłacona',
  incomplete: 'Nieukończona',
  incomplete_expired: 'Wygasła przed aktywacją',
};

const formatDateTime = (value) => {
  if (!value) return 'Brak danych';
  return new Intl.DateTimeFormat('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const isSubscriptionActive = (subscription) => {
  if (!subscription) return false;
  const entitlements = subscription.activeEntitlements || {};
  return Boolean(entitlements.poland || entitlements.poland_regions);
};

export default function SubscriptionPage() {
  const { user, loading, refreshRoles } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setChecking(false);
      return;
    }

    const fetchSubscription = async () => {
      setChecking(true);
      setError(null);
      try {
        const token = await auth.currentUser?.getIdToken(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubscription(response.data?.subscription || null);
        await refreshRoles();
      } catch (err) {
        setError(err.response?.data?.message || err.response?.data?.error || err.message);
      } finally {
        setChecking(false);
      }
    };

    fetchSubscription();
  }, [loading, user, refreshRoles]);

  if (loading || checking) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Paper sx={{ maxWidth: 640, mx: 'auto', my: 6, p: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={900} gutterBottom>
          Zaloguj się, żeby zobaczyć subskrypcję
        </Typography>
        <Button component={RouterLink} to="/login?redirect=%2Fkonto%2Fsubskrypcja" variant="contained" sx={{ mt: 2 }}>
          Zaloguj się
        </Button>
      </Paper>
    );
  }

  const active = isSubscriptionActive(subscription);
  const cancelReturned = searchParams.get('canceled') === '1';

  const startCancellation = async () => {
    setCancelling(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Musisz się zalogować, żeby anulować odnowienie.');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/billing/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data?.url) {
        throw new Error('Stripe nie zwrócił linku do anulowania odnowienia.');
      }

      window.location.assign(response.data.url);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
      setCancelling(false);
    }
  };

  return (
    <Paper sx={{ maxWidth: 760, mx: 'auto', my: 6, p: { xs: 3, md: 4 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={900}>
            Subskrypcja
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Status dostępu przypisanego do konta {user.email}.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {cancelReturned && (
          <Alert severity="info">
            Wróciłeś ze Stripe. Status odnowienia został zsynchronizowany z subskrypcją.
          </Alert>
        )}

        {!subscription ? (
          <Alert severity="info">
            Nie masz jeszcze aktywnej subskrypcji.
          </Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={active ? 'Dostęp aktywny' : 'Dostęp nieaktywny'}
                color={active ? 'success' : 'warning'}
              />
              <Chip label={statusLabels[subscription.status] || subscription.status} variant="outlined" />
            </Stack>

            <Divider />

            <Stack spacing={1.6}>
              <InfoRow label="Pakiet" value={planLabels[subscription.plan] || subscription.plan} />
              <InfoRow label="Rozliczenie" value={intervalLabels[subscription.plan] || 'Miesięczna'} />
              <InfoRow label="Kupiono" value={formatDateTime(subscription.createdAt)} />
              <InfoRow label="Aktualny okres od" value={formatDateTime(subscription.currentPeriodStart)} />
              <InfoRow label="Aktualny okres do" value={formatDateTime(subscription.currentPeriodEnd)} />
              <InfoRow
                label="Odnowienie"
                value={subscription.cancelAtPeriodEnd ? 'Wyłączone po końcu okresu' : 'Aktywne'}
              />
              <InfoRow label="Ostatnia aktualizacja" value={formatDateTime(subscription.updatedAt)} />
            </Stack>
          </>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button component={RouterLink} to="/vip" variant="contained">
            Zobacz pakiety
          </Button>
          {subscription && active && !subscription.cancelAtPeriodEnd && (
            <Button
              onClick={startCancellation}
              variant="outlined"
              color="error"
              disabled={cancelling}
            >
              {cancelling ? 'Przekierowuję do Stripe' : 'Anuluj odnowienie'}
            </Button>
          )}
          <Button component={RouterLink} to="/" variant="outlined">
            Wróć do strony głównej
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '210px 1fr' },
        gap: { xs: 0.25, sm: 2 },
      }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Typography fontWeight={800}>{value || 'Brak danych'}</Typography>
    </Box>
  );
}
